// Generated by Claude Code
import { State } from '#core/state/State.ts'
import LimitedAccessApi, { type LimitedAccessSlot } from '#core/api/operations/LimitedAccessApi.ts'
import { PermissionService, type PermissionAction } from '#core/entities/auth/roles/Permission.ts'
import { getFields, type EntityConstructor } from '#core/framework/decorators/field.ts'
import Asset from '#core/entities/operations/assets/Asset.ts'
import WorkOrder from '#core/entities/operations/work-orders/WorkOrder.ts'
import Part from '#core/entities/operations/parts/Part.ts'

/** One selectable entity property in the limited-access editor. */
export interface LimitedPropertyRow {
  /** Backend property name (the whitelist entry). */
  key: string
  /** i18n key (within {@link LimitedEntitySource.namespace}) when `@Field` metadata provides one. */
  labelKey?: string
}

/** Entity class + i18n namespace providing the property catalog for a limited-access service. */
export interface LimitedEntitySource {
  entity: EntityConstructor
  /** Locale namespace holding the entity's `columns.*` labels. */
  namespace: string
}

/**
 * Maps each limited-access service to the frontend entity class whose `@Field`
 * metadata seeds the property checklist. Work requests reuse the WorkOrder
 * entity (they are the same backend aggregate pre-approval).
 */
export const LIMITED_ENTITY_SOURCES: Partial<Record<PermissionService, LimitedEntitySource>> = {
  [PermissionService.ASSETS]: { entity: Asset, namespace: 'assets' },
  [PermissionService.WORK_ORDERS]: { entity: WorkOrder, namespace: 'work-orders' },
  [PermissionService.WORK_ORDER_REQUESTS]: { entity: WorkOrder, namespace: 'work-requests' },
  [PermissionService.PARTS]: { entity: Part, namespace: 'parts' },
}

const api = new LimitedAccessApi()

/**
 * Component state for the limited-access editor modal: loads the slot's
 * company-wide whitelist, builds the property catalog (entity `@Field`
 * metadata unioned with every whitelist already known for the service, so
 * configured properties never silently disappear), and persists edits.
 *
 * Instantiate via `useCustomState(() => new LimitedAccessEditorState(service, action))`;
 * remount the owning component per (service, action) pair.
 */
export class LimitedAccessEditorState extends State<LimitedAccessEditorState> {
  /** The slot being edited (effective view from the backend). */
  slot: LimitedAccessSlot | null = null
  /** The full selectable property catalog, metadata order first, extras appended. */
  properties: LimitedPropertyRow[] = []
  /** The currently selected (whitelisted) property keys. */
  selected: string[] = []
  loading = true
  saving = false
  loadError = false
  saveError = false

  private readonly _service: PermissionService
  private readonly _action: PermissionAction

  constructor(service: PermissionService, action: PermissionAction) {
    super()
    this._service = service
    this._action = action
    void this._load()
  }

  /** Toggles one property key in the selection. */
  toggle(key: string): void {
    this.reactTo.selected = this.selected.includes(key)
      ? this.selected.filter((k) => k !== key)
      : [...this.selected, key]
  }

  /** Adds all given (currently visible) keys to the selection. */
  selectAll(keys: string[]): void {
    this.reactTo.selected = [...new Set([...this.selected, ...keys])]
  }

  /** Removes all given (currently visible) keys from the selection. */
  clear(keys: string[]): void {
    this.reactTo.selected = this.selected.filter((k) => !keys.includes(k))
  }

  /**
   * Persists the selection as the company-wide whitelist for the slot.
   * Invokes `onSaved` only on success.
   */
  async save(onSaved: () => void): Promise<void> {
    this.reactTo.saving = true
    this.reactTo.saveError = false
    try {
      const result = await api.upsertConfig(this._service, this._action, this.selected)
      if (!result) {
        this.reactTo.saveError = true
        return
      }
      this.reactTo.slot = result
      onSaved()
    } catch {
      this.reactTo.saveError = true
    } finally {
      this.reactTo.saving = false
    }
  }

  /**
   * Removes the company override, restoring the code default, then reloads the
   * slot so the editor shows the default whitelist. Invokes `onReset` on success.
   */
  async resetToDefault(onReset: () => void): Promise<void> {
    this.reactTo.saving = true
    this.reactTo.saveError = false
    try {
      await api.deleteConfig(this._service, this._action)
      await this._load()
      onReset()
    } catch {
      this.reactTo.saveError = true
    } finally {
      this.reactTo.saving = false
    }
  }

  private async _load(): Promise<void> {
    this.reactTo.loading = true
    this.reactTo.loadError = false
    try {
      const slots = await api.listConfig()
      const slot = slots?.find((s) => s.service === this._service && s.action === this._action) ?? null
      if (!slot) {
        this.reactTo.loadError = true
        return
      }
      this.reactTo.slot = slot
      this.reactTo.properties = this._buildCatalog(slots ?? [], slot)
      this.reactTo.selected = [...slot.properties]
    } catch {
      this.reactTo.loadError = true
    } finally {
      this.reactTo.loading = false
    }
  }

  /**
   * Builds the selectable catalog: the entity's `@Field` metadata (in
   * declaration order), unioned with every property any whitelist of this
   * service already exposes (covers services with sparse frontend metadata and
   * keeps configured-but-unknown properties visible). Extras sort alphabetically.
   */
  private _buildCatalog(slots: LimitedAccessSlot[], slot: LimitedAccessSlot): LimitedPropertyRow[] {
    const source = LIMITED_ENTITY_SOURCES[this._service]
    const rows: LimitedPropertyRow[] = source
      ? getFields(source.entity).map((field) => ({ key: field.key, labelKey: field.labelKey }))
      : []
    const known = new Set(rows.map((row) => row.key))
    const extras = new Set<string>()
    for (const s of slots) {
      if (s.service !== slot.service) continue
      for (const key of s.properties) {
        if (!known.has(key)) extras.add(key)
      }
    }
    return [...rows, ...[...extras].sort().map((key) => ({ key }))]
  }
}
